import axios from "axios";
import {
  GET_RULE_CONDITIONS_URL,
  ADD_RULE_CONDITION_URL,
  UPDATE_RULE_CONDITION_URL,
  DELETE_RULE_CONDITION_URL,
} from "@/utils/constants/constants";

export default {
  methods: {
    open_assign_question_modal(condition) {
      this.condition_for_assignment = condition;
      this.show_assign_modal = true;
    },

    close_assign_questions_modal() {
      this.show_assign_modal = false;
      this.condition_for_assignment = null;
      this.entity_type_id = null;
    },

    on_assignment_updated() {
      this.show_success("Question assigned successfully");
      if (this.selected_rule?.id) {
        this.get_rule_conditions(this.selected_rule.id);
      }
      this.close_assign_questions_modal();
    },

    async get_rule_conditions(ruleId) {
      this.is_loading_conditions = true;
      this.rule_conditions = [];
      try {
        const response = await axios.get(`${GET_RULE_CONDITIONS_URL}?rule_id=${ruleId}`, {
          headers: this.computed_headers,
        });
        if (response.data.status === "success") {
          const conditions = (response.data.conditions || []).map(condition => ({
            id: condition.id,
            rule_id: condition.compliance_rule,
            condition_type: condition.condition_type,
            operator: condition.condition_operator,
            value: condition.value || "",
            comparison_value: condition.value_max || "",
            selected_options: condition.selected_options || [],
            logical_operator: "AND",
            compliance_field: condition.compliance_field || null,
            result_action_question: condition.result_action_question || null,
            checklist_item_assignment: condition.checklist_item_assignment || null,
            version: condition.version || 1,
            created_at: condition.date_created,
            updated_at: condition.date_created,
          }));

          this.rule_conditions = conditions.sort((a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return dateB - dateA;
          });

          this.$nextTick(() => {
            this.initialize_data_table();
          });
        } else {
          this.show_error(response.data.message || "Failed to load conditions");
        }
      } catch (error) {
        this.show_error("Error fetching conditions: " + error.message);
      } finally {
        this.is_loading_conditions = false;
      }
    },

    validate_form() {
      this.errors = {};

      if (!this.condition_form.condition_type) {
        this.errors.condition_type = "Condition type is required";
      }

      if (!this.condition_form.operator) {
        this.errors.operator = "Operator is required";
      }

      return Object.keys(this.errors).length === 0;
    },

    async save_condition() {
      if (this.is_saving_condition || !this.validate_form() || !this.csrfToken) return;
      this.is_saving_condition = true;
      try {
        const payload = {
          compliance_rule: parseInt(this.condition_form.rule_id, 10),
          condition_type: parseInt(this.condition_form.condition_type, 10),
          condition_operator: parseInt(this.condition_form.operator, 10),
          version: parseInt(this.condition_form.version, 10) || 1,
        };

        if (this.condition_form.value) {
          payload.value = this.condition_form.value;
        }

        if (this.condition_form.comparison_value) {
          payload.value_max = this.condition_form.comparison_value;
        }

        if (this.condition_form.selected_options && this.condition_form.selected_options.length > 0) {
          payload.selected_options = this.condition_form.selected_options;
        }

        if (this.condition_form.compliance_field) {
          payload.compliance_field = parseInt(this.condition_form.compliance_field, 10);
        }

        if (this.condition_form.result_action_question) {
          payload.result_action_question = parseInt(this.condition_form.result_action_question, 10);
        }

        if (this.condition_form.checklist_item_assignment) {
          payload.checklist_item_assignment = parseInt(
            this.condition_form.checklist_item_assignment,
            10
          );
        }

        if (this.condition_form.id) {
          payload.id = this.condition_form.id;
        }

        const url = this.condition_form.id ? UPDATE_RULE_CONDITION_URL : ADD_RULE_CONDITION_URL;
        const response = await axios.post(url, payload, { headers: this.computed_headers });

        if (response.data.status === "success") {
          this.show_success("Condition saved successfully");
          this.close_condition_modal();
          await this.get_rule_conditions(this.selected_rule.id);
        } else {
          this.show_error(response.data.message || "Failed to save condition");
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.errors ||
          error.message ||
          "Failed to save condition";
        this.show_error(
          typeof errorMessage === "string" ? errorMessage : JSON.stringify(errorMessage)
        );
      } finally {
        this.is_saving_condition = false;
      }
    },

    reset_condition_form() {
      this.condition_form = {
        id: null,
        rule_id: this.selected_rule?.id || null,
        condition_type: "",
        operator: "",
        value: "",
        comparison_value: "",
        logical_operator: "AND",
        selected_options: [],
        compliance_field: null,
        result_action_question: null,
        checklist_item_assignment: null,
        version: 1,
      };
      this.errors = {};
    },

    confirm_delete_condition(condition) {
      this.condition_to_delete = condition;
      if (this.deleteConditionModal) this.deleteConditionModal.show();
    },

    async delete_condition() {
      if (!this.condition_to_delete || !this.csrfToken) return;
      this.is_deleting = true;
      try {
        const response = await axios.post(
          DELETE_RULE_CONDITION_URL,
          { id: this.condition_to_delete.id },
          { headers: this.computed_headers }
        );
        if (response.data.status === "success") {
          this.show_success("Condition deleted successfully");
          this.close_delete_modal();
          await this.get_rule_conditions(this.selected_rule.id);
        } else {
          this.show_error(response.data.message || "Failed to delete condition");
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.errors ||
          error.message ||
          "Failed to delete condition";
        this.show_error(
          typeof errorMessage === "string" ? errorMessage : JSON.stringify(errorMessage)
        );
      } finally {
        this.is_deleting = false;
      }
    },
  },
};
